'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { useRegionLocale } from 'contexts/RegionLocaleContext';
import { analytics } from 'lib/analytics/analytics';
import styles from './RegionSelector.module.css';

export const RegionSelector = ({
  className,
  displayFormat = 'iso',
  theme = 'light',
  compactOnMobile = false,
}: {
  className?: string;
  displayFormat?: 'iso' | 'name';
  theme?: 'dark' | 'light';
  /**
   * Hides the country label (keeping the flag) below the header's mobile
   * breakpoint via CSS only — the label always renders in the DOM, so SSR and
   * the first client render stay identical (no hydration mismatch).
   */
  compactOnMobile?: boolean;
}) => {
  const { countryName, flag, region } = useRegionLocale();
  const router = useRouter();
  const pathname = usePathname() ?? '/';
  const search = useSearchParams()?.toString();

  const handleClick = () => {
    analytics.track('Region Selector - CLICK');
    const currentPath = search ? `${pathname}?${search}` : pathname;
    router.push(
      `/choose-country-region?ref=${encodeURIComponent(currentPath)}`,
    );
  };

  const displayName =
    displayFormat === 'name' ? countryName || region : region;

  return (
    <button
      className={[styles.trigger, className].filter(Boolean).join(' ')}
      onClick={handleClick}
      data-variant={theme}
      data-compact-mobile={compactOnMobile}
    >
      {flag && (
        <Image
          src={flag}
          alt={`${region} flag`}
          width={18}
          height={18}
          style={{ borderRadius: '50%' }}
        />
      )}
      <span className={styles.label}>{displayName}</span>
    </button>
  );
};
